/**
 * Project-readiness model — the wire shape returned by
 * `GET /api/mobile/v1/projects/:projectId/readiness` and computed by the
 * shared `lib/project-readiness.ts` (which also powers the web Overview
 * dashboard).
 *
 * This is the SINGLE source of truth for the model's shape: the server lib
 * produces it (its item literals `satisfies ReadinessItem`), and the mobile
 * client parses responses with `ProjectReadinessModelSchema`.
 *
 * Navigation is platform-neutral: items carry a semantic `target`, never a
 * URL. Web maps targets to hrefs; mobile maps them to navigator routes.
 */
import { z } from "zod";

export const READINESS_STATUSES = [
  "ready",
  "missing",
  "blocked",
  "recommended",
  "done",
  "review",
] as const;

export const ReadinessStatusSchema = z.enum(READINESS_STATUSES);
export type ReadinessStatus = z.infer<typeof ReadinessStatusSchema>;

export const ReadinessTargetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("company-settings") }),
  z.object({
    kind: z.literal("edit"),
    anchor: z.enum(["project-details", "people", "site", "safety"]).optional(),
  }),
  z.object({ kind: z.literal("workers"), review: z.boolean() }),
  z.object({ kind: z.literal("subcontractors"), review: z.boolean() }),
  z.object({ kind: z.literal("documents") }),
  z.object({ kind: z.literal("generate"), doc: z.enum(["cpp", "rams", "site_induction"]) }),
  z.object({
    kind: z.literal("setup"),
    returnTo: z.literal("rams"),
    // Optional for responses from older servers; the current readiness model
    // supplies the sections so mobile can choose the editor that owns each gap.
    missingSections: z.array(z.enum([
      "project-details", "client-details", "site-safety", "key-contacts",
    ])).optional(),
  }),
  z.object({ kind: z.literal("f10") }),
  z.object({ kind: z.literal("incidents") }),
  z.object({ kind: z.literal("account-workers") }),
]);
export type ReadinessTarget = z.infer<typeof ReadinessTargetSchema>;

export const ReadinessItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  detail: z.string(),
  cta: z.string(),
  status: ReadinessStatusSchema,
  reason: z.string().optional(),
  docs: z.array(z.string()).optional(),
  target: ReadinessTargetSchema,
});
export type ReadinessItem = z.infer<typeof ReadinessItemSchema>;

export const ReadinessFlagsSchema = z.object({
  showRequiredDetailsSetup: z.boolean(),
  showWorkforceReview: z.boolean(),
  showSubcontractorControls: z.boolean(),
  canShowPrimaryCppAction: z.boolean(),
  canStartCpp: z.boolean(),
  hasCpp: z.boolean(),
  isDomesticLightweight: z.boolean(),
  keepCurrentNeedsAction: z.boolean(),
  cppHeroTitle: z.string(),
  lightweightCppHeroTitle: z.string(),
  cppHeroCta: z.string(),
  lightweightCppHeroCta: z.string(),
});
export type ReadinessFlags = z.infer<typeof ReadinessFlagsSchema>;

export const ProjectReadinessModelSchema = z.object({
  requiredDetails: z.array(ReadinessItemSchema),
  missingRequiredDetails: z.array(ReadinessItemSchema),
  readyRequiredDetails: z.array(ReadinessItemSchema),
  people: z.array(ReadinessItemSchema),
  documents: z.array(ReadinessItemSchema),
  keepCurrent: z.array(ReadinessItemSchema),
  flags: ReadinessFlagsSchema,
});
export type ProjectReadinessModel = z.infer<typeof ProjectReadinessModelSchema>;
